import React from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { func, string, bool, exact } from "prop-types";
import { useTranslation } from "@lib/i18n";

import { Button } from "@components/shared/Button";
import RequestButton from "@components/shared/RequestButton";
import Typography from "@components/shared/Typography";
import SimpleButton from "@components/shared/SimpleButton";
import Radio from "@components/shared/Radio";
import TextField from "@components/shared/TextField";

import { usePhoneRegexPatternSelector } from "@redux/reducers/appConfig";
import { updateDeliveryContactDataAction } from "@redux/actions/dealActions";

const DeliveryContactSelect = ({
  onNextState,
  onPrevState,
  setIsDeliveryContactSelected,
  setDeliveryContactInfo,
  deliveryContactInfo,
  isDeliveryContactSelected,
  initialDeliveryContactInfo,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const phoneRegexp = usePhoneRegexPatternSelector();
  const { handleSubmit, register } = useForm({
    defaultValues: deliveryContactInfo,
  });

  const onFormSubmit = async data => {
    const nextDeliveryContactInfo = {
      ...deliveryContactInfo,
      ...data,
    };

    setDeliveryContactInfo(nextDeliveryContactInfo);

    dispatch(updateDeliveryContactDataAction(nextDeliveryContactInfo));

    onNextState();
  };

  const onConfirmationButtonClick = async () => {
    setDeliveryContactInfo(initialDeliveryContactInfo);

    await dispatch(updateDeliveryContactDataAction(null));
  };
  return (
    <div className="flex flex-col w-full gap-4">
      <SimpleButton className="px-0 py-2 m-0" onClick={onPrevState} withArrow>
        {t("basket.back")}
      </SimpleButton>
      <Typography className="text-sm uppercase">
        {t("basket.delivery.deliveryContact.description")}
      </Typography>

      <Typography className="font-semibold uppercase">
        {t("basket.delivery.selectTitle")}
      </Typography>

      <Radio
        dataId="basket-delivery-delivery-contact-notUsed"
        label={t("basket.delivery.deliveryContact.notUsed")}
        onChange={() => setIsDeliveryContactSelected(false)}
        checked={isDeliveryContactSelected === false}
      />

      <Radio
        dataId="basket-delivery-delivery-contact-used"
        label={t("basket.delivery.deliveryContact.used")}
        onChange={() => setIsDeliveryContactSelected(true)}
        checked={isDeliveryContactSelected === true}
      />

      {isDeliveryContactSelected && (
        <form id="deliveryContactForm" onSubmit={handleSubmit(onFormSubmit)}>
          <TextField
            placeholder={t("basket.delivery.deliveryContact.firstName")}
            name="firstName"
            ref={register}
            label={t("basket.delivery.deliveryContact.firstName")}
            errorHelperMessage={t(
              "basket.delivery.deliveryContactHelperTexts.firstName"
            )}
          />
          <TextField
            placeholder={t("basket.delivery.deliveryContact.lastName")}
            name="lastName"
            label={t("basket.delivery.deliveryContact.lastName")}
            required
            ref={register}
            errorHelperMessage={t(
              "basket.delivery.deliveryContactHelperTexts.lastName"
            )}
          />
          <TextField
            placeholder={t("basket.delivery.deliveryContact.phone")}
            name="phone"
            label={t("basket.delivery.deliveryContact.phone")}
            required
            ref={register}
            type="tel"
            pattern={phoneRegexp}
            errorHelperMessage={t(
              "basket.delivery.deliveryContactHelperTexts.phone"
            )}
          />
          <TextField
            placeholder={t("basket.delivery.deliveryContact.email")}
            name="email"
            label={t("basket.delivery.deliveryContact.email")}
            required
            ref={register}
            type="email"
            errorHelperMessage={t(
              "basket.delivery.deliveryContactHelperTexts.email"
            )}
          />
          <div className="flex flex-col justify-center items-center w-full pb-1 mt-4">
            <Button type="submit">{t("basket.delivery.continue")}</Button>
          </div>
        </form>
      )}

      {!isDeliveryContactSelected && (
        <div className="flex flex-col justify-center items-center w-full pb-1">
          <RequestButton
            disabled={isDeliveryContactSelected === null}
            triggerRequestCallback={onConfirmationButtonClick}
            type="button"
            onRequestSuccess={onNextState}
          >
            {t("basket.delivery.continue")}
          </RequestButton>
        </div>
      )}
    </div>
  );
};

DeliveryContactSelect.propTypes = {
  setIsDeliveryContactSelected: func.isRequired,
  setDeliveryContactInfo: func.isRequired,

  deliveryContactInfo: exact({
    firstName: string,
    lastName: string,
    phone: string,
    email: string,
  }).isRequired,

  initialDeliveryContactInfo: exact({
    firstName: string,
    lastName: string,
    phone: string,
    email: string,
  }).isRequired,

  isDeliveryContactSelected: bool,
  onNextState: func,
  onPrevState: func,
};

export default DeliveryContactSelect;
